// Linked runs (spec docs/specs/linked-runs-bulk-sort.md, mig 20261288000000).
//
// A BULK_SORT run sorts delivery pallets into bulk-sort pallets (bins); a
// packing-line run (PACK) packs them, one product per run. A business picks,
// per commodity, one run per variety (the old rule) or one run per commodity:
// such a run's variety is MIXED and each tip and pallet line keeps its own.
//
// The rules here mirror the server's (retip_bin, runs_linked_runs_guard) so
// the floor hears the refusal before the write; the server is the real check.

export const MIXED_VARIETY = 'MIXED'

export type RunGrain = 'VARIETY' | 'COMMODITY'

export interface LinkedRunFields {
  run_kind?: string | null
  run_number?: string | null
  grower?: string | null
  variety?: string | null
  commodity?: string | null
  feed_grade?: string | null
  feed_size_code?: string | null
}

const norm = (s?: string | null) => (s ?? '').trim().toLowerCase()

export const isBulkSortRun = (run?: { run_kind?: string | null } | null) => run?.run_kind === 'BULK_SORT'

/** A run of every variety of its commodity (runs.variety = MIXED). A received
 *  pallet's receipt run also says MIXED; it is not a packing run. */
export const isMixedRun = (run?: { run_kind?: string | null; variety?: string | null } | null) =>
  !!run && run.run_kind !== 'EXTERNAL_RECEIPT' && (run.variety ?? '').trim().toUpperCase() === MIXED_VARIETY

/** A run of every variety as the one variety picked for this pallet (New
 *  Pallet, From line, Repack); any other run, or none picked yet, unchanged. */
export function asPickedVariety<R extends { run_kind?: string | null; variety: string }>(run: R, picked: string | null): R {
  return picked && isMixedRun(run) ? { ...run, variety: picked } : run
}

/** The variety a new run is started with: MIXED when its commodity runs per
 *  commodity, else the one variety picked (more than one is refused). */
export function runVarietyFor(grain: RunGrain, varieties: string[]): { variety: string } | { error: string } {
  if (grain === 'COMMODITY') return { variety: MIXED_VARIETY }
  const distinct = Array.from(new Set(varieties.map(v => v.trim()).filter(Boolean)))
  if (distinct.length > 1) {
    return { error: `A run can only contain one variety. You picked ${distinct.length}: ${distinct.join(', ')}` }
  }
  return { variety: distinct[0] ?? '' }
}

/** New run's variety: a DUO run keeps the first pick's (its own rule); a
 *  per-commodity run is MIXED; a per-variety run takes the one variety picked,
 *  or the first selection's when nothing was ticked (an empty manual run). */
export function newRunVariety(o: { grain: RunGrain; duo: boolean; picked: string[]; first: string }):
  { variety: string } | { error: string } {
  if (o.duo) return { variety: o.first }
  if (o.grain === 'COMMODITY') return { variety: MIXED_VARIETY }
  if (o.picked.length === 0) return { variety: o.first }
  return runVarietyFor('VARIETY', o.picked)
}

export interface StoredBinFruit {
  bin_number: string
  grower: string
  variety: string
  grade?: string | null
  size_code?: string | null
  /** The commodity of the run that made the bin (bins carry no commodity). */
  source_commodity?: string | null
  source_run_kind?: string | null
}

/** Can this stored bin (a bulk-sort pallet) tip onto this run? The same order
 *  as retip_bin: bulk back onto bulk, grower, then variety or commodity. */
export function binRefusal(bin: StoredBinFruit, run: LinkedRunFields): { title: string; message: string } | null {
  if (bin.source_run_kind === 'BULK_SORT' && isBulkSortRun(run)) {
    return {
      title: 'Not onto a bulk sort',
      message: `${bin.bin_number} is a bulk-sort pallet. It goes onto a packing-line run, not back onto a bulk sort.`,
    }
  }
  if (norm(bin.grower) !== norm(run.grower)) {
    return {
      title: "Not this run's fruit",
      message: `${bin.bin_number} is ${bin.grower}'s fruit. ${run.run_number ?? 'This run'} is ${run.grower}'s. Pick another bin or another run.`,
    }
  }
  if (isMixedRun(run)) {
    if (bin.source_commodity && run.commodity && norm(bin.source_commodity) !== norm(run.commodity)) {
      return {
        title: "Not this run's fruit",
        message: `${bin.bin_number} is ${bin.source_commodity}. ${run.run_number ?? 'This run'} takes ${run.commodity}. Pick another bin or another run.`,
      }
    }
    return null
  }
  if (norm(bin.variety) && norm(bin.variety) !== norm(run.variety)) {
    return {
      title: "Not this run's fruit",
      message: `${bin.bin_number} is ${bin.grower} · ${bin.variety}. ${run.run_number ?? 'This run'} is ${run.grower} · ${run.variety}. Pick another bin or another run.`,
    }
  }
  return null
}

/** T5: the warning before a bulk-sort pallet of another class or size than
 *  the line's feed is tipped (allowed, and logged by retip_bin). */
export function feedWarning(bin: StoredBinFruit, run: LinkedRunFields): string | null {
  if (run.run_kind && run.run_kind !== 'PACK') return null
  const gradeOff = !!run.feed_grade && norm(bin.grade) !== norm(run.feed_grade)
  const sizeOff = !!run.feed_size_code && norm(bin.size_code) !== norm(run.feed_size_code)
  if (!gradeOff && !sizeOff) return null
  const want = [run.feed_grade ? `Class ${run.feed_grade}` : null, run.feed_size_code ? `size ${run.feed_size_code}` : null]
    .filter(Boolean).join(' · ')
  const got = [`Class ${bin.grade?.trim() || '?'}`, `size ${bin.size_code?.trim() || '?'}`].join(' · ')
  return `This line packs ${want}. ${bin.bin_number} is ${got}. Tip anyway? It will be noted on the tip.`
}

/** A bulk-sort pallet as the Rebin page captures it. */
export interface BulkSortDraft {
  crates: string
  variety: string
  /** The tare of ONE crate of the type picked (Bin Types); null = none set. */
  crateTareKg?: number | null
}

/** The Bulk sort mode's own checks, before anything is uploaded: crates on
 *  every pallet, and on a run of every variety, which variety it holds (T4:
 *  one variety per bulk-sort pallet). */
export function validateBulkSortDrafts(drafts: BulkSortDraft[], mixed: boolean, palletBaseKg: string): { ok: true } | { ok: false; message: string } {
  const base = Number(palletBaseKg)
  if (!palletBaseKg.trim() || !Number.isFinite(base) || base < 0) {
    return { ok: false, message: 'Type the pallet base tare (kg): the empty pallet under the crates.' }
  }
  for (let i = 0; i < drafts.length; i++) {
    const n = Number(drafts[i].crates)
    if (!Number.isInteger(n) || n <= 0) return { ok: false, message: `Bulk-sort pallet #${i + 1}: how many crates are on it?` }
    if (mixed && !drafts[i].variety.trim()) return { ok: false, message: `Bulk-sort pallet #${i + 1}: which variety is on it?` }
    if (!(Number(drafts[i].crateTareKg) > 0)) {
      return { ok: false, message: `Bulk-sort pallet #${i + 1}: that crate has no tare yet. Ask an admin to set it in Config → Storage → Bin types.` }
    }
  }
  return { ok: true }
}

/** A bulk-sort pallet's tare (Thomas, 2 Nov): each crate's own tare, by the
 *  crate type picked, plus the one empty pallet under them. */
export function bulkSortTareKg(crates: string, crateTareKg: number | null, palletBaseKg: string): number {
  const kg = (Number(crates) || 0) * (crateTareKg ?? 0) + (Number(palletBaseKg) || 0)
  return Math.round(kg * 100) / 100
}

/** The bins columns a bulk-sort pallet adds. crates_remaining starts full;
 *  the server sets it too (bins_crates_default). */
export function bulkSortRowFields(d: BulkSortDraft, runVariety: string, teamId = '') {
  const crates = Number(d.crates)
  return {
    crate_count: crates, crates_remaining: crates, variety: d.variety.trim() || runVariety,
    // Phase 3 (T7): the team that sorted it, when team-pallet piece work is on.
    ...(teamId ? { packed_by_team_id: teamId } : {}),
  }
}

/** A bulk-sort pallet that can be split between lines (T6): it carries crates
 *  and has more than one left. */
export function canSplit(bin: { crate_count?: number | null; crates_remaining?: number | null }): boolean {
  return !!bin.crate_count && (bin.crates_remaining ?? 0) > 1
}

/** What a portion tip left on a bulk-sort pallet, for the Reprint notice
 *  (Thomas, 2 Nov); null when the pallet went whole. */
export function cratesLeftAfterTip(before: number | null | undefined, crates: number | undefined): number | null {
  if (!crates || before == null || crates >= before) return null
  return before - crates
}

/** A bulk-sort pallet part-tipped and still holding crates: its sticker shows
 *  an old count, so the bin page offers Reprint. null otherwise. */
export function partTippedCratesLeft(bin: { crate_count?: number | null; crates_remaining?: number | null }): number | null {
  const left = bin.crates_remaining ?? 0
  return bin.crate_count && left > 0 && left < bin.crate_count ? left : null
}

/** The crates typed for this portion: a whole number from 1 to what is left. */
export function parsePortionCrates(input: string, left: number): { crates: number } | { error: string } {
  const n = Number(input.trim())
  if (!Number.isInteger(n) || n < 1 || n > left) return { error: `Type a whole number of crates from 1 to ${left}.` }
  return { crates: n }
}

/** The confirm before a stored bin is tipped; a portion says how many of how
 *  many crates and about how many kg (the server works the exact kg out). */
export function tipBinQuestion(
  bin: { bin_number: string; net_weight_kg: number; crates_remaining?: number | null },
  runNumber: string, crates?: number,
): string {
  const left = bin.crates_remaining ?? 0
  if (crates && crates < left) {
    const kg = Math.round((bin.net_weight_kg * crates) / left)
    return `Tip ${crates} of the ${left} crates on ${bin.bin_number} (about ${kg} kg net) into ${runNumber}? The rest stays in stock.`
  }
  return `Tip ${bin.bin_number} (${Math.round(bin.net_weight_kg)} kg net) into ${runNumber}?`
}

/** The active runs a stripped received-pallet bin may tip into (retip_bin's
 *  J4 rule, mig 20261288000000): its own variety, or a run of every variety
 *  (the server checks the fruit). A bin with no variety keeps every run. */
export function runsTakingVariety<R extends { variety: string; run_kind?: string | null }>(runs: R[], variety: string | null | undefined): R[] {
  const v = (variety ?? '').trim().toLowerCase()
  if (!v) return runs
  return runs.filter(r => isMixedRun(r) || r.variety.trim().toLowerCase() === v)
}

/** Load out as is (mig 20261340000000, Thomas 4 Nov): a bulk-sort pallet
 *  still in stock with crates left can go out as a final pallet unpacked. */
export function canLoadOutAsIs(bin: { crate_count?: number | null; crates_remaining?: number | null; status: string }): boolean {
  return !!bin.crate_count && (bin.crates_remaining ?? 0) > 0 && (bin.status === 'IN_COLDROOM' || bin.status === 'REBINNED')
}

/** The runs a bulk-sort pallet can be loaded out on: active packing-line
 *  runs that name their product (it sets the price and packaging) and take
 *  this pallet's fruit (load_out_bulk_sort_pallet's and retip_bin's rules). */
export function loadOutRuns<R extends LinkedRunFields & { status?: string | null; product_id: string | null }>(
  runs: R[], bin: StoredBinFruit): R[] {
  return runs.filter(r => r.run_kind === 'PACK' && r.status === 'ACTIVE' && !!r.product_id && !binRefusal(bin, r))
}

/** J6, count fruit once: across runs, a run's tipped kg is its intake (the
 *  server's intake_kg, mig 20261290000000: tipped kg less bulk-sort pallet
 *  tips). A row read without intake_kg keeps its tipped kg. */
export function countOnce<T extends { tipped_net_weight_kg: number | null; intake_kg?: number | null }>(rows: readonly T[]): T[] {
  return rows.map(r => (r.intake_kg == null ? r : { ...r, tipped_net_weight_kg: Number(r.intake_kg) }))
}

/** The Pallets page's run list: every packing run, and a bulk sort only while
 *  it still has final pallets. A run packed first and made a bulk sort later
 *  (RUN-2026-004, mig 20261313000000) keeps the pallets it already had, and
 *  they still need placing, approving and dispatching. PostgREST `or` filter. */
export const PALLETS_RUN_LIST_FILTER = 'run_kind.neq.BULK_SORT,pallet_count.gt.0'

/** What a bulk sort's Pallets page says instead of New Pallet. */
export const BULK_SORT_PALLETS_NOTE =
  'This is a bulk sort: it makes bulk-sort pallets on the Rebin page. Its older final pallets are listed here to place, approve and dispatch.'

/** A bulk sort packs nothing, so its packout is not a packout: averages of
 *  run packout leave it out (J6). */
export const packsFruit = (r: { run_kind?: string | null }) => r.run_kind !== 'BULK_SORT'

/** A run's packout as the grower portal shows it: a bulk sort says so. */
export function runPackoutLabel(r: { run_kind?: string | null; packout_percent: number | null }): string {
  if (!packsFruit(r)) return 'Bulk sort'
  return r.packout_percent != null ? `${Number(r.packout_percent).toFixed(1)}%` : '—'
}
